import { Translate } from '@chipinside/translate'
import { Box, lighten, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import React from 'react'

import { MammaryQuarter } from '#/@types/backend'

/**
 * Mapa dos quartos mamários (úbere) em grade 2x2.
 * Cada quadrado representa um quarto e é preenchido em laranja quando presente em `quarters`.
 */
const TypeMammaryQuarters: React.FC<MammaryQuartersProps> = ({ squareSize = 13, value = [] }) => {
  /**
   * A API pode retornar os quartos mamários de duas formas:
   *   1. Array de strings (ex: ["left-front", "right-back"])
   *   2. Array de objetos (ex: [{ status: "left-front" }, { status: "right-back" }]).
   *
   * A função `getStatusesFromValue` normaliza ambos os formatos para um array de strings com os slugs dos quartos.
   */
  const activeStatuses = getStatusesFromValue(value)

  return (
    <Box
      sx={{
        '&:hover .mammary-quarter': { opacity: 0.4 },
        '&:hover .mammary-quarter:hover': { opacity: 1 },
        'display': 'grid',
        'gap': 0.25,
        'gridTemplateColumns': 'repeat(2, auto)',
      }}
    >
      {layout.map(({ labelSide, status }) => (
        <Quarter
          active={activeStatuses.includes(status)}
          key={status}
          labelSide={labelSide}
          size={squareSize}
          status={status}
        />
      ))}
    </Box>
  )
}
interface IQuarter {
  abbreviation?: string
  description?: string
  status: MammaryQuarter
  text?: string
}

const getStatusesFromValue = (value): MammaryQuarter[] => {
  const first = value?.[0]

  // Verificação extrema para evitar erros da API
  return first && typeof first === 'object' && 'status' in first
    ? (value as IQuarter[]).map(({ status }) => status)
    : (value as MammaryQuarter[])
}

/**
 * Posição fixa de cada quarto na grade 2x2 e o lado em que o rótulo aparece.
 * Frente em cima, traseira embaixo; esquerda à esquerda, direita à direita.
 */
const layout: { labelSide: 'left' | 'right'; status: MammaryQuarter }[] = [
  { labelSide: 'left', status: 'left-front' },
  { labelSide: 'right', status: 'right-front' },
  { labelSide: 'left', status: 'left-back' },
  { labelSide: 'right', status: 'right-back' },
]

const Quarter: React.FC<{
  active: boolean
  labelSide: 'left' | 'right'
  size: number
  status: MammaryQuarter
}> = ({ active, labelSide, size, status }) => {
  const { palette } = useTheme()

  const filledColor = lighten(palette?.warning?.dark, 0.5)
  const emptyColor = palette?.text.secondary

  const abbreviation = Translate({ messageKey: `quarter_abbreviation_${status}` })
  const description = Translate({ messageKey: `quarter_description_${status}` })

  return (
    <Tooltip placement={labelSide} title={description} arrow>
      <Stack
        className="mammary-quarter"
        sx={{
          alignItems: 'center',
          flexDirection: labelSide === 'left' ? 'row' : 'row-reverse',
          gap: 0.5,
          justifyContent: 'flex-end',
          transition: 'opacity 0.2s',
        }}
      >
        <Typography
          sx={{
            color: active ? 'text.primary' : 'text.secondary',
            fontSize: 10.5,
            lineHeight: 'normal',
          }}
        >
          {abbreviation}
        </Typography>
        <Box
          sx={{
            backgroundColor: active ? filledColor : 'transparent',
            border: `1px solid ${active ? filledColor : emptyColor}`,
            borderRadius: 1,
            height: size,
            width: size,
          }}
        />
      </Stack>
    </Tooltip>
  )
}

export { TypeMammaryQuarters }

export interface MammaryQuartersProps {
  /** Quartos mamários afetados, conforme retornado pela API (apenas os slugs). */
  value?: MammaryQuarter[]
  /** Tamanho do lado de cada quadrado, em pixels. */
  squareSize?: number
}
